import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { ActivatedRoute, Router } from '@angular/router'
import { Observable, Subject, firstValueFrom } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'

import { LaskuToimittaja } from '@lemon/core-firebase/model/lasku'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'

import { ToimittajatSelaaDataSourceService } from './toimittajat-selaa.datasource.service'

interface HakuLomake {
  haku: string
  naytaPoistetut: boolean
}

@Component({
  templateUrl: './toimittajat-selaa.component.html',
  styleUrls: ['./toimittajat-selaa.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ToimittajatSelaaDataSourceService],
})
export class ToimittajatSelaaComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  readonly asiakas = signal<Asiakas | null>(null)

  lomake: FieldTree<HakuLomake>

  rivienMaaraObservable: Observable<number>
  lataaObservable: Observable<boolean>
  rivitObservable: Observable<LaskuToimittaja[]>

  displayedColumns = ['numero', 'nimi', 'ytunnus', 'iban', 'paivitetty', 'tila']

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _asiakasService: AsiakasService,
    public dataSource: ToimittajatSelaaDataSourceService,
  ) {
    this.rivienMaaraObservable = this.dataSource.rivienMaaraObservable
    this.lataaObservable = this.dataSource.lataaObservable
    this.rivitObservable = this.dataSource.nakyvatObservable
  }

  ngOnInit() {
    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      this.asiakas.set(asiakas)
    })

    this.lomake = form(signal<HakuLomake>({ haku: '', naytaPoistetut: false }), { injector: this._injector })

    arvonMuutokset(this.lomake.haku, { injector: this._injector })
      .pipe(debounceTime(300), takeUntil(this._ngUnsubscribe))
      .subscribe(arvo => {
        this.dataSource.setSearch(arvo ?? '')
      })

    arvonMuutokset(this.lomake.naytaPoistetut, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(arvo => {
        this.dataSource.setNaytaPoistetut(!!arvo)
      })
  }

  ngAfterViewInit() {
    const initial = this.dataSource.sort
    this.sort.sort({ id: initial.active, start: initial.direction === 'asc' ? 'asc' : 'desc', disableClear: true })
    this.sort.sortChange.pipe(takeUntil(this._ngUnsubscribe)).subscribe(sort => this.dataSource.setSort(sort))
    this.paginator.page.pipe(takeUntil(this._ngUnsubscribe)).subscribe(page => this.dataSource.setPage(page))
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  oletusIban(t: LaskuToimittaja): string {
    const ibanit = Object.values(t.iban ?? {})
    if (!ibanit.length) {
      return ''
    }
    const oletus = ibanit.find(i => i.tyyppi === 'oletus')
    return (oletus ?? ibanit[0]).iban ?? ''
  }

  ibanienMaara(t: LaskuToimittaja): number {
    return Object.keys(t.iban ?? {}).length
  }

  async avaa(rivi: LaskuToimittaja) {
    const avainTiedot = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    if (!avainTiedot?.avain) {
      return
    }
    this._router.navigate(['asiakkaat', avainTiedot.avain, 'toimittajat', 'selaa', rivi.avain])
  }
}
